import { useState, useEffect, useCallback } from 'react';
import { Layout } from '../../components/layout/Layout';
import api from '../../api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Plus, Minus, RefreshCw, Search, Download, ChevronLeft, ChevronRight,
  Network, Calendar, Filter, X
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDateARG } from '../../lib/utils';

const DiscoveryReport = () => {
  const [stats, setStats] = useState({ new: 0, gone: 0, modified: 0, total: 0, latest_scan: null });
  const [activeTab, setActiveTab] = useState('new');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Filters
  const [networks, setNetworks] = useState([]);
  const [filters, setFilters] = useState({
    network: '',
    date_from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    date_to: new Date().toISOString().split('T')[0],
    search: ''
  });
  
  // Data for each tab
  const [newsData, setNewsData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [goneData, setGoneData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [modifiedData, setModifiedData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  
  const [pageSize] = useState(25);

  // Fetch filter options
  const fetchFilterOptions = async () => {
    try {
      const response = await api.get('/reports/discovery/filter-options');
      setNetworks(response.data.networks || []);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.network) params.append('network', filters.network);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      
      const response = await api.get(`/reports/discovery/stats?${params}`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Fetch data based on active tab
  const fetchData = useCallback(async (tab, page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString()
      });
      if (filters.network) params.append('network', filters.network);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (filters.search) params.append('search', filters.search);
      
      let endpoint = '';
      let setData = null;
      
      switch (tab) {
        case 'new':
          endpoint = '/reports/discovery/news';
          setData = setNewsData;
          break;
        case 'gone':
          endpoint = '/reports/discovery/gone';
          setData = setGoneData;
          break;
        case 'modified':
          endpoint = '/reports/discovery/modified';
          setData = setModifiedData;
          break;
        default:
          return;
      }
      
      const response = await api.get(`${endpoint}?${params}`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [filters, pageSize]);

  // Initial load
  useEffect(() => {
    fetchFilterOptions();
  }, []);

  // Fetch stats when filters change
  useEffect(() => {
    fetchStats();
  }, [filters.network, filters.date_from, filters.date_to]);

  // Fetch data when tab or filters change
  useEffect(() => {
    fetchData(activeTab, 1);
  }, [activeTab, filters, fetchData]);

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      network: '',
      date_from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      date_to: new Date().toISOString().split('T')[0],
      search: ''
    });
  };

  // Export to Excel
  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({
        change_type: activeTab
      });
      if (filters.network) params.append('network', filters.network);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      
      const response = await api.get(`/reports/discovery/export?${params}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `discovery_${activeTab}_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Exportación completada');
    } catch (error) {
      toast.error('Error al exportar');
    } finally {
      setExporting(false);
    }
  };

  // Get current data based on tab
  const getCurrentData = () => {
    switch (activeTab) {
      case 'new': return newsData;
      case 'gone': return goneData;
      case 'modified': return modifiedData;
      default: return { items: [], total: 0, page: 1, pages: 1 };
    }
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    fetchData(activeTab, newPage);
  };

  // Format date
  const formatDate = (dateStr) => formatDateARG(dateStr);

  // Format ports JSON
  const formatPorts = (portsJson) => {
    if (!portsJson) return '-';
    try {
      const ports = JSON.parse(portsJson);
      if (!Array.isArray(ports) || ports.length === 0) return '-';
      return ports.map(p => `${p.port}/${p.service || '?'}`).join(', ');
    } catch {
      return '-';
    }
  };

  const currentData = getCurrentData();

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Cambios de Red</h1>
            <p className="page-subtitle">
              Hosts nuevos, removidos y modificados detectados por Discovery
              {stats.latest_scan && (
                <span className="ml-2 text-xs text-muted-foreground">
                  (Último scan: {formatDate(stats.latest_scan)})
                </span>
              )}
            </p>
          </div>
          <Button
            onClick={handleExport}
            disabled={exporting || currentData.total === 0}
            variant="outline"
            data-testid="export-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            {exporting ? 'Exportando...' : 'Exportar'}
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="discovery-stats">
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-primary/50 ${activeTab === 'new' ? 'ring-2 ring-green-500' : ''}`}
            onClick={() => setActiveTab('new')}
            data-testid="stats-new"
          >
            <Plus className="h-6 w-6 text-green-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-green-500">{stats.new}</div>
              <div className="stats-card-label">Nuevos</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-primary/50 ${activeTab === 'gone' ? 'ring-2 ring-red-500' : ''}`}
            onClick={() => setActiveTab('gone')}
            data-testid="stats-gone"
          >
            <Minus className="h-6 w-6 text-red-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-red-500">{stats.gone}</div>
              <div className="stats-card-label">Removidos</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-primary/50 ${activeTab === 'modified' ? 'ring-2 ring-yellow-500' : ''}`}
            onClick={() => setActiveTab('modified')}
            data-testid="stats-modified"
          >
            <RefreshCw className="h-6 w-6 text-yellow-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-yellow-500">{stats.modified}</div>
              <div className="stats-card-label">Modificados</div>
            </div>
          </div>
          <div className="stats-card flex items-center gap-3" data-testid="stats-total">
            <Network className="h-6 w-6 text-primary flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value">{stats.total}</div>
              <div className="stats-card-label">Total Cambios</div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <Card className="card-subtle">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Filtros:</span>
              </div>
              
              <div className="flex items-center gap-2">
                <Network className="h-4 w-4 text-muted-foreground" />
                <select
                  value={filters.network}
                  onChange={(e) => handleFilterChange('network', e.target.value)}
                  className="h-9 text-sm bg-background border border-border rounded px-3"
                  data-testid="filter-network"
                >
                  <option value="">Todas las subnets</option>
                  {networks.map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
              
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <Input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => handleFilterChange('date_from', e.target.value)}
                  className="h-9 w-36"
                  data-testid="filter-date-from"
                />
                <span className="text-muted-foreground">a</span>
                <Input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => handleFilterChange('date_to', e.target.value)}
                  className="h-9 w-36"
                  data-testid="filter-date-to"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <Search className="h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar IP, hostname, MAC..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="h-9 w-48"
                  data-testid="filter-search"
                />
              </div>
              
              {(filters.network || filters.search) && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="h-4 w-4 mr-1" />
                  Limpiar
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="new" data-testid="tab-new">
              <Plus className="h-4 w-4 mr-2" />
              Nuevos ({stats.new})
            </TabsTrigger>
            <TabsTrigger value="gone" data-testid="tab-gone">
              <Minus className="h-4 w-4 mr-2" />
              Removidos ({stats.gone})
            </TabsTrigger>
            <TabsTrigger value="modified" data-testid="tab-modified">
              <RefreshCw className="h-4 w-4 mr-2" />
              Modificados ({stats.modified})
            </TabsTrigger>
          </TabsList>

          {/* Tab: Nuevos */}
          <TabsContent value="new">
            <Card className="card-subtle overflow-hidden">
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
                  </div>
                ) : newsData.items.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Plus className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No se encontraron hosts nuevos en el período seleccionado</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="table-subtle">
                      <thead>
                        <tr>
                          <th>SUBNET</th>
                          <th>IP</th>
                          <th>HOSTNAME</th>
                          <th>MAC</th>
                          <th>VENDOR</th>
                          <th>TIPO</th>
                          <th>SISTEMA</th>
                          <th>PUERTOS</th>
                          <th>DETECTADO</th>
                        </tr>
                      </thead>
                      <tbody>
                        {newsData.items.map((item, idx) => (
                          <tr key={`${item.ip}-${idx}`}>
                            <td className="font-mono text-xs">{item.network}</td>
                            <td className="font-mono text-primary">{item.ip}</td>
                            <td>{item.hostname || '-'}</td>
                            <td className="font-mono text-xs">{item.mac || '-'}</td>
                            <td className="text-muted-foreground">{item.vendor || '-'}</td>
                            <td>
                              {item.tipo_estimado && (
                                <span className="badge badge-outline">{item.tipo_estimado}</span>
                              )}
                            </td>
                            <td className="text-xs">{item.sistema_estimado || '-'}</td>
                            <td className="text-xs font-mono">{formatPorts(item.ports_json)}</td>
                            <td className="text-xs text-muted-foreground">{formatDate(item.run_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab: Removidos */}
          <TabsContent value="gone">
            <Card className="card-subtle overflow-hidden">
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
                  </div>
                ) : goneData.items.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Minus className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No se encontraron hosts removidos en el período seleccionado</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="table-subtle">
                      <thead>
                        <tr>
                          <th>SUBNET</th>
                          <th>IP</th>
                          <th>HOSTNAME</th>
                          <th>MAC</th>
                          <th>VENDOR</th>
                          <th>TIPO</th>
                          <th>SISTEMA</th>
                          <th>ÚLTIMA VEZ VISTO</th>
                          <th>DETECTADO AUSENTE</th>
                        </tr>
                      </thead>
                      <tbody>
                        {goneData.items.map((item, idx) => (
                          <tr key={`${item.ip}-${idx}`}>
                            <td className="font-mono text-xs">{item.network}</td>
                            <td className="font-mono text-red-500">{item.ip}</td>
                            <td>{item.hostname || '-'}</td>
                            <td className="font-mono text-xs">{item.mac || '-'}</td>
                            <td className="text-muted-foreground">{item.vendor || '-'}</td>
                            <td>
                              {item.tipo_estimado && (
                                <span className="badge badge-outline">{item.tipo_estimado}</span>
                              )}
                            </td>
                            <td className="text-xs">{item.sistema_estimado || '-'}</td>
                            <td className="text-xs text-muted-foreground">{formatDate(item.last_seen)}</td>
                            <td className="text-xs text-muted-foreground">{formatDate(item.run_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab: Modificados */}
          <TabsContent value="modified">
            <Card className="card-subtle overflow-hidden">
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
                  </div>
                ) : modifiedData.items.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <RefreshCw className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No se encontraron hosts modificados en el período seleccionado</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="table-subtle">
                      <thead>
                        <tr>
                          <th>SUBNET</th>
                          <th>IP</th>
                          <th>HOSTNAME</th>
                          <th>TIPO</th>
                          <th>CAMBIOS</th>
                          <th>DETECTADO</th>
                        </tr>
                      </thead>
                      <tbody>
                        {modifiedData.items.map((item, idx) => (
                          <tr key={`${item.ip}-${idx}`}>
                            <td className="font-mono text-xs">{item.network}</td>
                            <td className="font-mono text-yellow-500">{item.ip}</td>
                            <td>
                              {item.hostname_old !== item.hostname_new ? (
                                <span>
                                  <span className="line-through text-muted-foreground">{item.hostname_old || '-'}</span>
                                  <span className="mx-1">→</span>
                                  <span className="text-primary">{item.hostname_new || '-'}</span>
                                </span>
                              ) : (
                                item.hostname_new || '-'
                              )}
                            </td>
                            <td>
                              {item.tipo_old !== item.tipo_new ? (
                                <span>
                                  <span className="badge badge-outline opacity-50">{item.tipo_old || '-'}</span>
                                  <span className="mx-1">→</span>
                                  <span className="badge badge-outline">{item.tipo_new || '-'}</span>
                                </span>
                              ) : (
                                item.tipo_new && <span className="badge badge-outline">{item.tipo_new}</span>
                              )}
                            </td>
                            <td className="text-xs max-w-md">
                              <span className="text-muted-foreground">{item.detail || '-'}</span>
                            </td>
                            <td className="text-xs text-muted-foreground">{formatDate(item.run_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Pagination */}
        {currentData.total > 0 && (
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              {currentData.total > 0 
                ? `${(currentData.page - 1) * pageSize + 1} - ${Math.min(currentData.page * pageSize, currentData.total)} de ${currentData.total}`
                : '0 registros'}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentData.page - 1)}
                disabled={currentData.page === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-muted-foreground">
                {currentData.page} / {currentData.pages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentData.page + 1)}
                disabled={currentData.page === currentData.pages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default DiscoveryReport;
